// OCR text detector via Tesseract.js.
//
// Runs against the same downscaled ImageData the QR and ArUco detectors
// see. Returns { kind: 'none' } while the worker is still cold-starting,
// so live scan stays quiet during the ~4MB traineddata download instead
// of queueing frames. Once ready, we filter Tesseract's line-level output
// by the caller's regex and cap results at MAX_HITS_PER_FRAME to keep
// the dispatch surface bounded on over-permissive patterns.

import type { DecodedSymbol } from '../types';
import type { DecodeOutcome } from './qrDecoder';
import {
  ensureTesseract,
  getTesseractStatus,
  runRecognizeWithWhitelist,
} from './ensureTesseract';
import { DEFAULT_TEXT_PATTERN, parseTextPattern, type ParsedTextPattern } from './textPattern';

// Dev-only per-frame perf log — avoids serializing OCR candidates (which
// may include incidental strings from monitors/paper) in prod, and cuts
// the ~0.5-2 ms/call stringify+devtools overhead that would contaminate
// benchmark detector timings.
const PERF_LOG_ENABLED = import.meta.env.DEV;

const MAX_HITS_PER_FRAME = 8;
// Tesseract's line.confidence is 0–100. Anything below this floor is
// rejected before it becomes a hit. PSM.SPARSE_TEXT + a digits-only
// whitelist is aggressive — it happily reads keyboard keys, wall
// textures, and tree bark as low-confidence 3-digit strings, and the
// regex alone can't tell those from real bin labels. 60 is a starting
// point; tune from the [text-perf] log.
const DEFAULT_MIN_CONFIDENCE = 60;
const DEFAULT_PARSED = parseTextPattern(DEFAULT_TEXT_PATTERN);

export interface DecodeTextOptions {
  /** Preferred: parsed pattern from `parseTextPattern`. Falls back to the default digit pattern. */
  parsed?: ParsedTextPattern;
  /** Minimum Tesseract line confidence (0–100). Defaults to 60. */
  minConfidence?: number;
}

// Reused across frames to avoid a per-frame OffscreenCanvas allocation.
// Tesseract's ImageLike union doesn't include ImageData, so we have to
// paint into a canvas before recognize().
let scratchCanvas: OffscreenCanvas | HTMLCanvasElement | null = null;

function toCanvas(imageData: ImageData): OffscreenCanvas | HTMLCanvasElement {
  const w = imageData.width;
  const h = imageData.height;
  if (!scratchCanvas || scratchCanvas.width !== w || scratchCanvas.height !== h) {
    scratchCanvas =
      typeof OffscreenCanvas !== 'undefined'
        ? new OffscreenCanvas(w, h)
        : Object.assign(document.createElement('canvas'), { width: w, height: h });
  }
  const ctx = scratchCanvas.getContext('2d') as
    | CanvasRenderingContext2D
    | OffscreenCanvasRenderingContext2D
    | null;
  if (!ctx) throw new Error('2d context unavailable for OCR');
  ctx.putImageData(imageData, 0, 0);
  return scratchCanvas;
}

export async function decodeText(
  imageData: ImageData,
  opts?: DecodeTextOptions
): Promise<DecodeOutcome> {
  const parsed = opts?.parsed ?? DEFAULT_PARSED;
  const minConfidence = opts?.minConfidence ?? DEFAULT_MIN_CONFIDENCE;
  const status = getTesseractStatus();
  if (status.state !== 'ready') {
    // Kick off (or resume) init in the background but return immediately —
    // don't queue frames behind the cold-start.
    if (status.state === 'idle') void ensureTesseract().catch(() => undefined);
    return { kind: 'none' };
  }
  const image = toCanvas(imageData);
  const { data } = await runRecognizeWithWhitelist(image, parsed.whitelist, { blocks: true });
  const symbols: DecodedSymbol[] = [];
  const blocks = data.blocks ?? [];
  const debug: Array<{ text: string; conf: number; verdict: 'hit' | 'regex' | 'conf' }> | null =
    PERF_LOG_ENABLED ? [] : null;
  let rejectedByConfidence = 0;
  outer: for (const block of blocks) {
    for (const para of block.paragraphs) {
      for (const line of para.lines) {
        const text = line.text.trim();
        if (!text) continue;
        const passesRegex = parsed.regex.test(text);
        const passesConfidence = line.confidence >= minConfidence;
        const verdict: 'hit' | 'regex' | 'conf' = !passesRegex
          ? 'regex'
          : !passesConfidence
            ? 'conf'
            : 'hit';
        if (debug) debug.push({ text, conf: +line.confidence.toFixed(1), verdict });
        if (verdict === 'conf') rejectedByConfidence += 1;
        if (verdict !== 'hit') continue;
        symbols.push({
          text,
          bbox: {
            x1: line.bbox.x0,
            y1: line.bbox.y0,
            x2: line.bbox.x1,
            y2: line.bbox.y1,
          },
          orientation: 0,
          kind: 'text',
        });
        if (symbols.length >= MAX_HITS_PER_FRAME) break outer;
      }
    }
  }
  if (debug) {
    console.debug(
      '[text-perf]',
      JSON.stringify({
        pattern: parsed.regex.source,
        minConfidence,
        candidates: debug.slice(0, 12),
        hits: symbols.length,
        rejectedByConfidence,
      })
    );
  }
  return symbols.length ? { kind: 'symbols', symbols } : { kind: 'none' };
}
